/**
 * SWARM BEHAVIOR — the drone archetypes as executable movement.
 * Spec: bible 00-CANON-BRIEF §3.5 (drone doctrine), 05-BESTIARY §0.5.
 *
 * DATA MODEL. These functions operate directly on EnemySystem's
 * struct-of-arrays. An earlier version of this file used an object-per-agent
 * interface, which could not be wired into the runtime without either copying
 * every entity each frame or rewriting the entity store. SoA-native is the
 * correct shape: no allocation in the hot loop, cache-friendly iteration, and
 * it drops straight into the existing update.
 *
 * Every archetype teaches a DIFFERENT dodging skill. That is the whole reason
 * there are several rather than one with scaled health:
 *
 *   Flock    — positioning against a mass rather than an object
 *   Lance    — lateral commitment; leave before the column arrives
 *   Kamikaze — threat triage under time pressure
 *   Curtain  — gap-reading, the core skill of the genre
 *   Relay    — kill-order literacy
 *
 * The coordination is genuinely emergent — neighbours are read, formations hold,
 * buffs propagate — but it is all local rules producing global shape, with zero
 * model calls and full determinism.
 */

/** Tunables. Named so the debug GUI can expose them without magic numbers. */
export const SWARM = {
  flock: {
    cohesion: 1.4,
    separation: 2.6,
    alignment: 0.9,
    neighborRadius: 3.4,
    maxSpeed: 7.2,
    /** Downward bias so a flock always advances against the scroll. */
    advance: 3.2,
    /** Lateral drift toward the player — gentle, so it herds rather than homes. */
    pursuit: 0.55,
  },
  lance: { speed: 9.5, fileSpacing: 1.9, weave: 1.6 },
  /** Lock delay is >= 400 ms by law — locking at the start is unreadable. */
  kamikaze: { hoverSeconds: 1.6, lockDelayMs: 450, diveSpeed: 24, hoverDrift: 1.1 },
  curtain: { driftSpeed: 1.8, latticeSpacing: 2.9, descend: 2.4 },
  relay: { radius: 6.0, cadenceBonus: 0.35 },
} as const

/** Spatial cell size for neighbour lookup. ~= flock neighbour radius. */
const CELL = 3.5

/**
 * Flock step for every active TICK, using a uniform grid so neighbour search is
 * O(n) rather than O(n^2). At 40 drones the naive version is 1,600 distance
 * tests per frame; this is closer to 200.
 */
export function stepFlock(
  count: number,
  active: Uint8Array,
  role: Uint8Array,
  flockRole: number,
  x: Float32Array,
  y: Float32Array,
  vx: Float32Array,
  vy: Float32Array,
  dt: number,
  playerX: number,
  grid: Map<number, number[]>,
): void {
  const cfg = SWARM.flock

  // --- rebuild the spatial grid -------------------------------------------
  grid.clear()
  for (let i = 0; i < count; i++) {
    if (!active[i] || role[i] !== flockRole) continue
    const key = (Math.floor(x[i] / CELL) << 16) ^ (Math.floor(y[i] / CELL) & 0xffff)
    const cell = grid.get(key)
    if (cell) cell.push(i)
    else grid.set(key, [i])
  }

  const r2 = cfg.neighborRadius * cfg.neighborRadius

  for (let i = 0; i < count; i++) {
    if (!active[i] || role[i] !== flockRole) continue

    let cx = 0
    let cy = 0
    let sx = 0
    let sy = 0
    let ax = 0
    let ay = 0
    let n = 0

    // Sample the 3x3 neighbourhood of cells.
    const gx = Math.floor(x[i] / CELL)
    const gy = Math.floor(y[i] / CELL)
    for (let ox = -1; ox <= 1; ox++) {
      for (let oy = -1; oy <= 1; oy++) {
        const cell = grid.get(((gx + ox) << 16) ^ ((gy + oy) & 0xffff))
        if (!cell) continue
        for (const j of cell) {
          if (j === i) continue
          const dx = x[j] - x[i]
          const dy = y[j] - y[i]
          const d2 = dx * dx + dy * dy
          if (d2 > r2 || d2 < 1e-6) continue
          cx += x[j]
          cy += y[j]
          // Separation falls off sharply so crowding is punished.
          sx -= dx / d2
          sy -= dy / d2
          ax += vx[j]
          ay += vy[j]
          n++
        }
      }
    }

    if (n > 0) {
      cx = cx / n - x[i]
      cy = cy / n - y[i]
      ax = ax / n - vx[i]
      ay = ay / n - vy[i]
      vx[i] += (cx * cfg.cohesion + sx * cfg.separation + ax * cfg.alignment) * dt
      vy[i] += (cy * cfg.cohesion + sy * cfg.separation + ay * cfg.alignment) * dt
    }

    // The flock leans toward the player without homing — it should feel like
    // being surrounded, not chased.
    vx[i] += Math.sign(playerX - x[i]) * cfg.pursuit * dt
    vy[i] -= cfg.advance * dt

    const sp = Math.hypot(vx[i], vy[i])
    if (sp > cfg.maxSpeed) {
      vx[i] = (vx[i] / sp) * cfg.maxSpeed
      vy[i] = (vy[i] / sp) * cfg.maxSpeed
    }
    x[i] += vx[i] * dt
    y[i] += vy[i] * dt
  }
}

/**
 * Kamikaze: hover, then lock, then commit.
 *
 * The lock is captured at the END of the delay, never at the start. Locking
 * early means the reticle lies about where the dive will go and the player
 * cannot read it. This is the difference between "hard" and "unfair".
 *
 * Returns true on the frame the lock fires, so the caller can play the tell.
 */
export function stepKamikaze(
  i: number,
  x: Float32Array,
  y: Float32Array,
  vx: Float32Array,
  vy: Float32Array,
  age: Float32Array,
  locked: Uint8Array,
  dt: number,
  playerX: number,
  playerY: number,
): boolean {
  const cfg = SWARM.kamikaze
  if (!locked[i]) {
    const lockAt = cfg.hoverSeconds + cfg.lockDelayMs / 1000
    if (age[i] >= lockAt) {
      // Capture NOW — this is the honest moment.
      const dx = playerX - x[i]
      const dy = playerY - y[i]
      const d = Math.hypot(dx, dy) || 1
      vx[i] = (dx / d) * cfg.diveSpeed
      vy[i] = (dy / d) * cfg.diveSpeed
      locked[i] = 1
      x[i] += vx[i] * dt
      y[i] += vy[i] * dt
      return true
    }
    // Hover with a slight drift so it reads as alive, not parked.
    x[i] += Math.sin(age[i] * 2.4 + i) * cfg.hoverDrift * dt
    y[i] -= 2.2 * dt
    return false
  }
  // Committed: straight line, no correction. Always out-runnable.
  x[i] += vx[i] * dt
  y[i] += vy[i] * dt
  return false
}

/**
 * Lance: a single-file column that weaves as one body. Followers inherit the
 * leader's lateral phase on a delay, so the whole file traces the same path.
 */
export function stepLance(
  i: number,
  slot: number,
  x: Float32Array,
  y: Float32Array,
  baseX: Float32Array,
  age: Float32Array,
  dt: number,
): void {
  const cfg = SWARM.lance
  const phase = age[i] - slot * 0.14
  x[i] = baseX[i] + Math.sin(phase * 1.7) * cfg.weave
  y[i] -= cfg.speed * dt
}

/**
 * Curtain: a rigid lattice with an authored missing member. The gap is a slot
 * index, not an emergent property, so its width is exactly provable offline.
 */
export function stepCurtain(
  i: number,
  x: Float32Array,
  y: Float32Array,
  baseX: Float32Array,
  age: Float32Array,
  dt: number,
): void {
  const cfg = SWARM.curtain
  x[i] = baseX[i] + Math.sin(age[i] * 0.7) * cfg.driftSpeed
  y[i] -= cfg.descend * dt
}

/**
 * Relay buffs: everything inside a relay's radius fires faster. Returns a
 * multiplier for entity `i`. This is what makes killing the Relay first feel
 * CORRECT rather than merely optimal.
 */
export function relayMultiplier(
  i: number,
  count: number,
  active: Uint8Array,
  role: Uint8Array,
  relayRole: number,
  x: Float32Array,
  y: Float32Array,
): number {
  const r2 = SWARM.relay.radius * SWARM.relay.radius
  for (let j = 0; j < count; j++) {
    if (!active[j] || role[j] !== relayRole || j === i) continue
    const dx = x[j] - x[i]
    const dy = y[j] - y[i]
    if (dx * dx + dy * dy <= r2) return 1 + SWARM.relay.cadenceBonus
  }
  return 1
}
